import { SxProps, Theme } from '@mui/material/styles';
import { CSSProperties } from 'react';

import type { SystemStyleObject } from '@mui/system';

// Theme constants - aligned with ThemeObject.ts where values match exactly
import { BLACK, DARK_GREY, RED, SCOUT_BLUE, SCOUT_TEAL, WHITE } from '../ThemeContext/constants';

import type { AutoFillProviderKind } from './types';

type IStyle = {
  [className: string]: CSSProperties | IStyle;
};

// WCAG-specific color tokens that differ from or extend the global theme constants.
// TODO: once design stabilises, migrate these to CSS custom properties (:root vars
// in wcag.scss) so SCSS and TS share a single source of truth.

const REQUIRED_RED = '#F86262';
const TEAL_HOVER = '#4ba8a3';
const PLACEHOLDER_GREY = '#989898';
const BORDER_GREY = '#ECECEC';
const BORDER_GREY_ALT = '#e0e0e0';
const CHECKBOX_UNCHECKED_GREY = '#DDDDDD';
const BG_GREY = '#f5f5f5';
const BG_GREY_HOVER = '#e8e8e8';
const ABOUT_US_BG = '#F5F7F8';
const SEPARATOR_GREY = '#999999';
const CLEAR_ICON_COLOR = RED;
const TEAL_FOCUS_SHADOW = 'rgba(91, 196, 191, 0.25)';
const ERROR_FOCUS_SHADOW = 'rgba(227, 125, 122, 0.25)';
const WCAG_MAX_CONTENT_WIDTH = '864px';

// Preview-specific tokens
const INFO_BANNER_BG = '#e5f6fd';
const INFO_BANNER_TEXT = '#014361';
const INFO_BANNER_ICON = '#0288D1';
const EMPTY_VALUE_GREY = DARK_GREY;
const WCAG_TEXT_DARK = '#333333';
const WCAG_ICON_GREY = '#AAAAAA';
const AUTO_FILL_BODY = DARK_GREY;
const AUTO_FILL_CARD_BG = '#F3F3F3';
const AUTO_FILL_CARD_BG_ACTIVE = '#ECECEC';
const AUTO_FILL_ICON = DARK_GREY;
const AUTO_FILL_CLEAR_ICON = '#D6827D';
const AUTO_FILL_HELPER = '#727272';
const AUTO_FILL_OR = '#1F1F1F';
const AUTO_FILL_SEEK = '#E60272';
const AUTO_FILL_INDEED = '#2557A7';
const AUTO_FILL_LINKEDIN = '#0177B5';
const AUTO_FILL_ACTION = SCOUT_TEAL;
const AUTO_FILL_UPLOAD_WIDTH = '416px';
const AUTO_FILL_SINGLE_UPLOAD_WIDTH = '598px';
const ATTACHMENT_CONTROL_HEIGHT = '39px';
const TLP_HEADER_FOREGROUND_VISIBLE_QUERY = '@media (min-width: 1440px)';
const TLP_HEADER_FOREGROUND_COLUMN_WIDTH = 'min(400px, 28vw)';
const TLP_HEADER_FOREGROUND_LAYOUT_WIDTH = 'calc(100vw - 128px)';
const TLP_HEADER_TEXT_MAX_WIDTH = {
  xs: '100%',
  md: '720px',
  lg: '760px'
};

// Unified validation presentation across Questions, Attachments, SourceSelection,
// and CandidateDetails (imperative phone/email errors); ref MultiTagInput.tsx Collapse pattern.
// TODO: heavy !important beats HAML page-level selectors (#job *); tracked for cleanup.

const VALIDATION_ERROR_COLOR = '#e37d7a';
const WCAG_FALLBACK_FONT_FAMILY = 'Montserrat, sans-serif';
const WCAG_DEFAULT_FONT_FAMILY = `var(--tlp-font-family, ${WCAG_FALLBACK_FONT_FAMILY})`;
const VALIDATION_FONT_FAMILY = "'Source Sans Pro', sans-serif";
const TLP_HEADER_COLOR = `var(--tlp-header-color, ${SCOUT_BLUE})`;
const TLP_SUB_HEADER_COLOR = `var(--tlp-sub-header-color, ${DARK_GREY})`;
const TLP_HEADER_OVERLAY = 'var(--tlp-header-overlay, rgba(8, 77, 109, 0.7))';
const TLP_BANNER_HERO_TEXT = `var(--tlp-banner-hero-text, ${WHITE})`;
const TLP_SECTION_HEADING_COLOR = `var(--tlp-section-heading-color, ${TLP_HEADER_COLOR})`;
const TLP_BUTTON_COLOR = `var(--tlp-button-color, ${SCOUT_TEAL})`;
const TLP_BUTTON_HOVER_COLOR = `var(--tlp-button-hover-color, ${TEAL_HOVER})`;
const TLP_BUTTON_TEXT_COLOR = `var(--tlp-button-text-color, ${WHITE})`;
const TLP_SECONDARY_BUTTON_COLOR = `var(--tlp-secondary-button-color, ${BG_GREY})`;
const TLP_SECONDARY_BUTTON_HOVER_COLOR = `var(--tlp-secondary-button-hover-color, ${BG_GREY_HOVER})`;
const TLP_SECONDARY_BUTTON_TEXT_COLOR = `var(--tlp-secondary-button-text-color, ${BLACK})`;
const TLP_SECONDARY_BUTTON_OUTLINE_COLOR = TLP_SECONDARY_BUTTON_TEXT_COLOR;
const TLP_BUTTON_RADIUS = 'var(--tlp-button-radius, 8px)';
const TLP_ABOUT_BG = `var(--tlp-about-bg, ${ABOUT_US_BG})`;
const TLP_ABOUT_TEXT = `var(--tlp-about-text, ${WCAG_TEXT_DARK})`;
const TLP_HEADING_FONT_FAMILY = `var(--tlp-heading-font-family, ${WCAG_FALLBACK_FONT_FAMILY})`;
const TLP_FONT_FAMILY = WCAG_DEFAULT_FONT_FAMILY;

/** Error message container for MUI Collapse reveal; !important beats HAML
 *  page-level descendant selectors (.application-form p/div rules). */
const validationErrorMessageSx = (): SxProps<Theme> => ({
  color: `${VALIDATION_ERROR_COLOR} !important`,
  fontFamily: `${VALIDATION_FONT_FAMILY} !important`,
  fontSize: '13px !important',
  fontWeight: '400 !important',
  lineHeight: '1.5 !important',
  margin: '8px 0 0 0 !important'
});

const validationInvalidBorderColor = VALIDATION_ERROR_COLOR;

/** Imperative CSS text for DOM-created error elements (CandidateDetails phone/email). */
const validationErrorCssText = `color: ${VALIDATION_ERROR_COLOR}; font-family: ${VALIDATION_FONT_FAMILY}; font-size: 13px; font-weight: 400; margin-top: 8px; line-height: 1.5;`;

/** Focus ring for inputs — teal by default, red on error. `important` appends
 *  !important for high-specificity HAML-injected contexts; not needed standalone. */
const focusRing = (hasError = false, important = true) => {
  const i = important ? ' !important' : '';
  return {
    borderColor: `${hasError ? VALIDATION_ERROR_COLOR : SCOUT_TEAL}${i}`,
    boxShadow: `${hasError ? ERROR_FOCUS_SHADOW : TEAL_FOCUS_SHADOW} 0 0 0 2px${i}`,
    outline: `none${i}`
  };
};

/** Shared base for all WCAG action buttons. Variant overrides color/bg/hover. */
const buttonBase = {
  fontWeight: 600,
  lineHeight: 1.2,
  borderRadius: TLP_BUTTON_RADIUS,
  textTransform: 'none',
  cursor: 'pointer',
  border: 'none',
  boxShadow: 'none',
  transition: 'background-color 0.2s ease'
};

type ActionButtonVariant = 'primary' | 'secondary';
type ActionButtonPreset = 'form' | 'preview';

/** Standard section container used by Questions, SourceSelection, Attachments, Preview.
 *  Bottom margin 80px aligns edit-mode section rhythm with preview's `preview.sectionSx`. */
const sectionContainerBase = {
  maxWidth: WCAG_MAX_CONTENT_WIDTH,
  margin: '0 auto 80px',
  padding: '24px 0'
};

/** Standard section heading (h2) used by multiple sections. */
const sectionHeadingBase = {
  fontWeight: '700 !important',
  fontSize: '24px',
  color: TLP_SECTION_HEADING_COLOR,
  margin: '0 0 32px 0 !important',
  padding: '0 !important'
};

const actionButtonPresetStyles = {
  form: {
    padding: '12px 32px',
    minHeight: '39px',
    // Required to beat the `#job * { font-size: inherit }` cascade at
    // app/assets/stylesheets/wcag.scss:520-523 which would otherwise force 16px.
    fontSize: '12px !important'
  },
  preview: {
    display: 'inline-flex !important',
    width: 'auto !important',
    flex: '0 0 auto !important',
    padding: '12px 32px',
    minHeight: '39px',
    fontSize: '12px !important',
    borderRadius: TLP_BUTTON_RADIUS
  }
};

const actionButtonVariantStyles = {
  primary: {
    color: TLP_BUTTON_TEXT_COLOR,
    backgroundColor: TLP_BUTTON_COLOR
  },
  secondary: {
    color: TLP_SECONDARY_BUTTON_TEXT_COLOR,
    backgroundColor: TLP_SECONDARY_BUTTON_COLOR,
    border: `1px solid ${TLP_SECONDARY_BUTTON_OUTLINE_COLOR}`
  }
};

const actionButtonHoverStyles = {
  primary: {
    backgroundColor: TLP_BUTTON_HOVER_COLOR,
    boxShadow: 'none'
  },
  secondary: {
    backgroundColor: TLP_SECONDARY_BUTTON_HOVER_COLOR,
    borderColor: TLP_SECONDARY_BUTTON_OUTLINE_COLOR,
    boxShadow: 'none'
  }
};

const actionButtonFocusVisibleStyles = {
  primary: {
    outline: `2px solid ${TLP_BUTTON_COLOR}`,
    outlineOffset: '2px'
  },
  secondary: {
    outline: `2px solid ${TLP_SECONDARY_BUTTON_OUTLINE_COLOR}`,
    outlineOffset: '2px'
  }
};

const buildActionButtonSx = ({
  variant,
  preset = 'form',
  fontFamily,
  includeFocusVisible = false,
  includeActive = false,
  overrides = {}
}: {
  variant: ActionButtonVariant;
  preset?: ActionButtonPreset;
  fontFamily?: string;
  includeFocusVisible?: boolean;
  includeActive?: boolean;
  overrides?: SystemStyleObject<Theme>;
}): SxProps<Theme> => ({
  ...buttonBase,
  ...actionButtonPresetStyles[preset],
  ...actionButtonVariantStyles[variant],
  ...(fontFamily ? { fontFamily } : {}),
  '&:hover': actionButtonHoverStyles[variant],
  ...(includeFocusVisible
    ? {
        '&:focus-visible': actionButtonFocusVisibleStyles[variant]
      }
    : {}),
  ...(includeActive
    ? {
        '&:active': actionButtonHoverStyles[variant]
      }
    : {}),
  ...overrides
});

const saveDialogCopyBase = {
  fontFamily: WCAG_DEFAULT_FONT_FAMILY,
  color: DARK_GREY,
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '20px'
};

const dialogPaperBaseSx = (): SxProps<Theme> => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  fontFamily: WCAG_DEFAULT_FONT_FAMILY,
  width: '425px',
  padding: '48px 32px',
  borderRadius: '6px',
  textAlign: 'center',
  boxSizing: 'border-box'
});

const dialogTitleBaseSx = (): SxProps<Theme> => ({
  fontFamily: TLP_HEADING_FONT_FAMILY,
  color: TLP_SECTION_HEADING_COLOR,
  fontSize: '24px',
  fontWeight: 700,
  lineHeight: '30px',
  padding: 0,
  marginBottom: '8px'
});

const dialogContentBaseSx = (): SxProps<Theme> => ({
  padding: 0,
  marginBottom: '24px'
});

const dialogActionsBaseSx = (): SxProps<Theme> => ({
  display: 'flex',
  justifyContent: 'center',
  padding: 0,
  gap: '8px'
});

const autoFillUploadWidth = (providerCount: number): string => {
  if (providerCount === 0) {
    return WCAG_MAX_CONTENT_WIDTH;
  }

  if (providerCount === 1) {
    return AUTO_FILL_SINGLE_UPLOAD_WIDTH;
  }

  return AUTO_FILL_UPLOAD_WIDTH;
};

const autoFillCardBackgroundColor = (hasError: boolean, isDragActive: boolean): string => {
  if (hasError) {
    return '#FFF7F7';
  }

  if (isDragActive) {
    return AUTO_FILL_CARD_BG_ACTIVE;
  }

  return AUTO_FILL_CARD_BG;
};

const autoFillCardBorderColor = (hasError: boolean, isDragActive: boolean): string => {
  if (hasError) {
    return VALIDATION_ERROR_COLOR;
  }

  if (isDragActive) {
    return SCOUT_TEAL;
  }

  return 'transparent';
};

const autoFillProviderBackgroundColor = (kind: AutoFillProviderKind): string => {
  if (kind === 'seek') {
    return AUTO_FILL_SEEK;
  }

  if (kind === 'linkedin') {
    return AUTO_FILL_LINKEDIN;
  }

  return AUTO_FILL_INDEED;
};

const autoFillProviderGap = (kind: AutoFillProviderKind): string => {
  if (kind === 'indeed') {
    return '16px';
  }

  if (kind === 'linkedin') {
    return '8px';
  }

  return '16px';
};

const autoFillProviderGridColumn = (
  kind: AutoFillProviderKind,
  providerCount: number
): SxProps<Theme>['gridColumn'] => {
  if (kind === 'linkedin' && providerCount === 3) {
    return { xs: 'auto', sm: '1 / -1', md: '1 / -1' };
  }

  return 'auto';
};

const autoFillProviderJustifySelf = (
  kind: AutoFillProviderKind,
  providerCount: number
): SxProps<Theme>['justifySelf'] => {
  if (kind === 'linkedin' && providerCount === 3) {
    return { xs: 'stretch', sm: 'center', md: 'center' };
  }

  return { xs: 'stretch', sm: 'start', md: 'start' };
};

const autoFillProviderMaxWidth = (
  kind: AutoFillProviderKind,
  providerCount: number
): SxProps<Theme>['maxWidth'] => {
  if (kind === 'linkedin' && providerCount === 3) {
    return { xs: '100%', sm: '232px', md: '232px' };
  }

  return { xs: '100%', sm: 'none', md: 'none' };
};

const autoFillProviderHoverBackgroundColor = (kind: AutoFillProviderKind): string => {
  if (kind === 'seek') {
    return '#C90064';
  }

  if (kind === 'linkedin') {
    return '#00669B';
  }

  return '#1C4585';
};

const autoFillProviderDisabledSx = (kind: AutoFillProviderKind): SystemStyleObject<Theme> => ({
  backgroundColor: autoFillProviderBackgroundColor(kind),
  color: WHITE,
  opacity: 0.64
});

const autoFillProviderFocusVisibleSx: SystemStyleObject<Theme> = {
  outline: `2px solid ${WHITE}`,
  outlineOffset: '2px',
  boxShadow: `0 0 0 4px ${TEAL_FOCUS_SHADOW}`
};

const autoFillProviderControlBaseSx = (
  kind: AutoFillProviderKind,
  providerCount: number
): SystemStyleObject<Theme> => ({
  ...buttonBase,
  minHeight: 'auto',
  width: {
    xs: '100%',
    sm: 'auto',
    md: 'auto'
  },
  padding: '12px 16px',
  borderRadius: TLP_BUTTON_RADIUS,
  backgroundColor: autoFillProviderBackgroundColor(kind),
  color: WHITE,
  fontFamily: `${WCAG_DEFAULT_FONT_FAMILY} !important`,
  fontSize: '14px !important',
  fontStyle: 'normal',
  fontWeight: '600 !important',
  lineHeight: 'normal !important',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: autoFillProviderGap(kind),
  textAlign: 'center',
  whiteSpace: 'nowrap',
  gridColumn: autoFillProviderGridColumn(kind, providerCount),
  justifySelf: autoFillProviderJustifySelf(kind, providerCount),
  maxWidth: autoFillProviderMaxWidth(kind, providerCount)
});

export const classes = {
  // Shared tokens and common styles
  shared: {
    requiredIndicatorStyle: {
      color: REQUIRED_RED,
      fontWeight: 400
    } as CSSProperties,

    selectFieldStyles: (hasError = false) => ({
      width: '100% !important',
      '& .MuiInputBase-input': {
        border: `1px solid ${hasError ? VALIDATION_ERROR_COLOR : BORDER_GREY} !important`,
        width: '100% !important',
        height: '50px !important',
        padding: '12px !important',
        boxSizing: 'border-box'
      },
      '& .MuiInputBase-input:focus, & .MuiInputBase-input:focus-visible': focusRing(hasError)
    }),

    // Kept in the render tree so native constraint validation (checkValidity /
    // reportValidity) still fires; unlike display:none the browser can focus it.
    validatableHiddenInputStyle: {
      position: 'absolute',
      opacity: 0,
      width: '1px',
      height: '1px',
      overflow: 'hidden',
      pointerEvents: 'none'
    } as CSSProperties
  },

  // Section heading — shared contract for every WCAG section h2 (edit + preview)
  sectionHeading: {
    rootSx: (
      fontFamily: string = TLP_HEADING_FONT_FAMILY,
      variant: 'default' | 'helperText' | 'compact' | 'inline' = 'default'
    ): SxProps<Theme> => ({
      ...sectionHeadingBase,
      fontFamily,
      // CandidateDetails embeds the required-indicator span in its heading;
      // centralising the selector keeps it harmless elsewhere and stops drift.
      '& .required-indicator': {
        color: REQUIRED_RED,
        fontWeight: 400
      },
      ...(variant === 'helperText' && {
        // Helper-text variant (Attachments edit mode): heading sits closer to
        // the hint below; the hint then owns the 32px gap to the first field.
        margin: '0 0 8px 0 !important'
      }),
      ...(variant === 'compact' && {
        color: TLP_ABOUT_TEXT,
        margin: '0 0 16px 0 !important'
      }),
      ...(variant === 'inline' && {
        // Inline variant (CandidateDetails heading row): the row owns the
        // section gap, so the h2 must not carry bottom margin into flex centring.
        margin: '0 !important'
      })
    })
  },

  // Questions component styles
  questions: {
    containerSx: (): SxProps<Theme> => ({
      ...sectionContainerBase
    }),

    questionItemSx: (fontFamily?: string, hasError = false): SxProps<Theme> => ({
      marginBottom: '24px !important',
      '& .MuiFormControl-root': {
        width: '100% !important'
      },
      '&:last-child': {
        marginBottom: 0
      },
      '& textarea': {
        width: '100% !important'
      },
      // Error treatment for all field types when invalid; high-specificity selectors
      // + !important beat per-field-type styles (rankFieldContainerSx, etc.).
      ...(hasError && {
        // Text / textarea / date / rank inputs
        '& .MuiFormControl-root .MuiInputBase-input, & .MuiInputBase-input, & .MuiInputBase-inputMultiline':
          {
            borderColor: `${VALIDATION_ERROR_COLOR} !important`,
            '&:focus, &:focus-visible': focusRing(true)
          },
        // Select dropdown
        '& .MuiSelect-select[role="combobox"]': {
          borderColor: `${VALIDATION_ERROR_COLOR} !important`,
          '&:focus, &:focus-visible': focusRing(true)
        },
        // Radio buttons and checkboxes — tint the control itself
        '& .MuiRadio-root, & .MuiCheckbox-root': {
          color: `${VALIDATION_ERROR_COLOR} !important`
        }
      })
    }),

    questionLabelSx: (
      fontFamily = WCAG_DEFAULT_FONT_FAMILY,
      variant: 'regular' | 'grouped' = 'regular'
    ): SxProps<Theme> => ({
      display: 'block',
      fontFamily,
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: 1.5,
      color: BLACK,
      marginBottom: variant === 'grouped' ? '24px !important' : '16px !important',
      '& .wcag-rich-text p': {
        margin: 0,
        fontWeight: 'inherit',
        fontSize: 'inherit',
        lineHeight: 'inherit'
      }
    }),

    errorTextSx: validationErrorMessageSx,

    inputStyles: (fontFamily = WCAG_DEFAULT_FONT_FAMILY): CSSProperties | IStyle => ({
      width: '100% !important',
      '& .MuiInputBase-root': {
        fontFamily,
        fontSize: '16px',
        width: '100% !important',
        padding: '0 !important'
      },
      '& .MuiInputBase-input, & .MuiInputBase-inputMultiline': {
        width: '100% !important',
        borderRadius: '8px',
        border: `1px solid ${BORDER_GREY}`,
        padding: '12px !important',
        fontSize: '16px',
        fontWeight: 400,
        color: BLACK,
        backgroundColor: WHITE,
        fontFamily,
        minHeight: '44px',
        boxSizing: 'border-box',
        '&::placeholder': {
          color: PLACEHOLDER_GREY
        },
        '&:focus, &:focus-visible': {
          border: `1px solid ${SCOUT_TEAL} !important`,
          ...focusRing(false, false)
        }
      }
    }),

    felixFieldContainerSx: (): SxProps<Theme> => ({
      display: 'flex',
      flexDirection: 'column'
    }),

    felixContainerSx: (): SxProps<Theme> => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      marginTop: '24px !important'
    }),

    felixReferenceSx: (): SxProps<Theme> => ({
      display: 'none'
    }),

    felixIconSx: (): SxProps<Theme> => ({
      marginRight: '8px'
    }),

    felixButtonSx: (fontFamily = WCAG_DEFAULT_FONT_FAMILY): SxProps<Theme> => ({
      width: 'fit-content',
      marginBottom: '4px',
      display: 'flex',
      alignItems: 'center',
      background: `linear-gradient(white, white) padding-box, linear-gradient(to right, #e548ff, ${SCOUT_TEAL}) border-box`,
      border: '2px solid transparent',
      borderRadius: '10px',
      padding: '4px 8px',
      fontFamily,
      fontSize: '14px !important',
      fontWeight: 700,
      color: '#4e4ec7',
      cursor: 'pointer',
      '&:hover': {
        color: '#363696 !important',
        transition: 'all 0.5s'
      }
    }),

    aiInterviewRootSx: (): SxProps<Theme> => ({
      marginTop: '10px'
    }),

    radioCheckboxGroupSx: (fontFamily = WCAG_DEFAULT_FONT_FAMILY): SxProps<Theme> => ({
      '& .MuiFormGroup-root, & .MuiRadioGroup-root': {
        // 24px between radio/checkbox options per design.
        gap: '24px',
        paddingLeft: '16px !important'
      },
      '& .MuiFormControlLabel-root': {
        marginLeft: 0,
        marginRight: 0,
        gap: '16px',
        '& .MuiTypography-root': {
          fontFamily,
          fontSize: '16px',
          color: BLACK,
          lineHeight: 1.5,
          marginLeft: '0 !important'
        },
        '& .wcag-rich-text p': {
          margin: 0,
          fontWeight: 'inherit',
          fontSize: 'inherit',
          lineHeight: 'inherit'
        }
      },
      // The `#job *` cascade (wcag.scss:520-523) plus radio rules at wcag.scss:905-920
      // can shrink radio icons and hide the ripple; keep overrides minimal (icon
      // size, native hit area/ripple).
      '& .MuiRadio-root': {
        padding: '0 !important',
        borderRadius: '50% !important',
        overflow: 'visible !important',
        '& .MuiSvgIcon-root': {
          width: '22px !important',
          height: '22px !important',
          fontSize: '22px !important'
        },
        '& .MuiTouchRipple-root': {
          color: `${SCOUT_TEAL} !important`,
          overflow: 'visible !important',
          top: '-9px !important',
          right: '-9px !important',
          bottom: '-9px !important',
          left: '-9px !important'
        },
        '& .MuiTouchRipple-child': {
          display: 'block !important',
          width: '100% !important',
          height: '100% !important',
          borderRadius: '50% !important',
          backgroundColor: `${SCOUT_TEAL} !important`
        }
      },
      '& .MuiCheckbox-root': {
        padding: '0 !important',
        '& .MuiSvgIcon-root': {
          width: '22px !important',
          height: '22px !important',
          fontSize: '22px !important'
        },
        '&.Mui-focusVisible, &:focus-visible': {
          outline: `2px solid ${SCOUT_TEAL} !important`,
          outlineOffset: '2px',
          borderRadius: '4px'
        }
      }
    }),

    rankFieldContainerSx: (
      fontFamily = WCAG_DEFAULT_FONT_FAMILY,
      hasError = false
    ): SxProps<Theme> => ({
      paddingLeft: '16px !important',
      '& > div > div': {
        // 24px between rank options per design (matches radio/checkbox gap).
        marginBottom: '24px !important',
        display: 'flex !important',
        alignItems: 'center !important',
        columnGap: '16px !important',
        marginLeft: '0 !important',
        marginTop: '0 !important',
        '&:last-child': {
          marginBottom: 0
        }
      },
      '& > div > div > .MuiFormControl-root': {
        flex: '0 0 60px !important',
        width: '60px !important',
        minWidth: '60px !important',
        maxWidth: '60px !important',
        marginRight: '0 !important'
      },
      // Override shared rankFieldSelect (width: 90%, marginRight: 20px)
      // so WCAG layout keeps a fixed compact dropdown + 16px text gap.
      '& > div > div > .MuiFormControl-root .MuiInputBase-root': {
        width: '60px !important',
        marginRight: '0 !important'
      },
      '& > div > div > .MuiFormControl-root .MuiInputBase-input': {
        minWidth: 'unset !important',
        width: '60px !important',
        boxSizing: 'border-box',
        borderRadius: '6px !important',
        border: `1px solid ${hasError ? VALIDATION_ERROR_COLOR : BORDER_GREY} !important`,
        backgroundColor: `${WHITE} !important`,
        color: `${WCAG_TEXT_DARK} !important`,
        fontFamily,
        fontSize: '16px !important',
        lineHeight: '24px !important',
        padding: '6px 28px 6px 10px !important',
        margin: '3px !important'
      },
      '& > div > div > .MuiFormControl-root .MuiInputBase-input:focus': {
        ...focusRing(hasError),
        borderRadius: '6px !important',
        backgroundColor: `${WHITE} !important`
      },
      '& > div > div > .MuiFormControl-root .MuiSvgIcon-root': {
        color: `${WCAG_ICON_GREY} !important`,
        marginRight: '10px !important'
      },
      '& > div > div > div:last-child': {
        width: 'auto !important',
        flex: '1 !important',
        fontFamily,
        fontSize: '16px',
        color: BLACK,
        marginLeft: '0 !important',
        '& .wcag-rich-text p': {
          margin: 0,
          fontWeight: 'inherit',
          fontSize: 'inherit',
          lineHeight: 'inherit'
        }
      }
    }),

    datePickerWrapperSx: (): SxProps<Theme> => ({
      width: '220px',
      '& .MuiFormControl-root': {
        width: '220px !important'
      }
    }),

    datePickerSx: (
      fontFamily = WCAG_DEFAULT_FONT_FAMILY,
      hasError = false,
      hasValue = false
    ): CSSProperties | IStyle => ({
      fontFamily,
      width: '220px !important',
      height: '50px !important',
      backgroundColor: WHITE,
      borderRadius: '8px',
      border: `1px solid ${hasError ? VALIDATION_ERROR_COLOR : BORDER_GREY} !important`,
      outline: 'none',
      padding: '0 !important',
      color: hasValue ? BLACK : PLACEHOLDER_GREY,
      display: 'flex',
      alignItems: 'center',
      boxSizing: 'border-box',
      letterSpacing: 'var(--tlp-letter-spacing, 0px) !important',
      '& .MuiPickersInputBase-root, & .MuiInputBase-root': {
        height: '50px !important',
        boxSizing: 'border-box'
      },
      '& fieldset': {
        border: 'none !important'
      },
      '&.Mui-focused': {
        border: `1px solid ${hasError ? VALIDATION_ERROR_COLOR : SCOUT_TEAL} !important`,
        ...focusRing(hasError, false)
      },
      '& .MuiPickersSectionList-root': {
        flex: '1 !important',
        marginLeft: '24px !important',
        padding: '0 !important',
        fontSize: '16px !important',
        lineHeight: '24px',
        width: 'auto !important',
        fontFamily,
        color: hasValue ? BLACK : PLACEHOLDER_GREY
      },
      '& input': {
        padding: '0 !important'
      },
      '& .MuiInputAdornment-root': {
        width: '24px',
        height: '24px',
        margin: '0 24px 0 24px !important',
        justifyContent: 'center',
        overflow: 'visible !important'
      },
      '& button': {
        outline: 'none'
      },
      '& .MuiSvgIcon-root': {
        width: '24px',
        height: '24px',
        color: DARK_GREY
      }
    })
  },

  // Header component styles
  header: {
    containerSx: (backgroundImageUrl?: string, forceBannerLayout = false): SxProps<Theme> => {
      const hasBackground = !!backgroundImageUrl;
      const hasBannerArtwork = hasBackground || forceBannerLayout;
      return {
        width: hasBannerArtwork ? '100vw !important' : '100%',
        margin: hasBannerArtwork ? '0 calc(50% - 50vw) 60px !important' : '0 auto 60px',
        maxWidth: hasBannerArtwork ? 'none !important' : WCAG_MAX_CONTENT_WIDTH,
        padding: hasBannerArtwork ? '120px 24px' : '24px 0',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        ...(hasBannerArtwork && {
          ...(hasBackground && {
            backgroundImage: `url("${backgroundImageUrl}")`,
            backgroundSize: {
              xs: 'cover',
              sm: 'cover',
              md: 'cover'
            },
            backgroundPosition: {
              xs: 'center 30%',
              sm: 'center 35%',
              md: 'center'
            },
            backgroundRepeat: 'no-repeat'
          }),
          ...(!hasBackground && {
            backgroundColor: TLP_HEADER_OVERLAY
          }),
          minHeight: {
            xs: '280px',
            sm: '320px',
            md: '400px'
          },
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          boxSizing: 'border-box'
        }),
        ...(!hasBannerArtwork && {
          maxWidth: WCAG_MAX_CONTENT_WIDTH
        }),
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: hasBackground ? TLP_HEADER_OVERLAY : 'transparent',
          zIndex: 1
        }
      };
    },

    backgroundImageSx: (imageUrl: string, isActive: boolean): SxProps<Theme> => ({
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 0,
      // The legacy WCAG reset uses `#job div { background: none transparent; }`,
      // so the layered carousel image must explicitly win that ID selector.
      backgroundImage: `url("${imageUrl}") !important`,
      backgroundSize: {
        xs: 'cover !important',
        sm: 'cover !important',
        md: 'cover !important'
      },
      backgroundPosition: {
        xs: 'center 30% !important',
        sm: 'center 35% !important',
        md: 'center !important'
      },
      backgroundRepeat: 'no-repeat !important',
      pointerEvents: 'none',
      opacity: isActive ? 1 : 0,
      transition: 'opacity 300ms ease-in-out',
      willChange: 'opacity',
      '@media (prefers-reduced-motion: reduce)': {
        transition: 'none'
      }
    }),

    foregroundImageSx: (isActive: boolean, isVisible: boolean): SxProps<Theme> => ({
      position: 'absolute',
      right: 0,
      bottom: 0,
      zIndex: isActive ? 0 : -1,
      display: 'none',
      width: 'auto',
      height: '100% !important',
      maxWidth: '100% !important',
      [TLP_HEADER_FOREGROUND_VISIBLE_QUERY]: {
        display: 'block'
      },
      objectFit: 'contain',
      opacity: isActive ? (isVisible ? 1 : 0.12) : 0,
      pointerEvents: 'none',
      transition: 'opacity 360ms cubic-bezier(0.4, 0, 0.2, 1)',
      willChange: 'opacity',
      '@media (prefers-reduced-motion: reduce)': {
        transition: 'none'
      }
    }),

    foregroundCarouselSx: (): SxProps<Theme> => ({
      position: 'relative',
      display: 'none',
      width: `${TLP_HEADER_FOREGROUND_COLUMN_WIDTH} !important`,
      maxWidth: '100% !important',
      height: {
        xs: '220px !important',
        sm: '280px !important',
        md: '340px !important',
        lg: '360px !important'
      },
      justifySelf: 'end',
      gridColumn: '3 / 4',
      zIndex: 1,
      pointerEvents: 'none',
      [TLP_HEADER_FOREGROUND_VISIBLE_QUERY]: {
        display: 'block'
      }
    }),

    textContentSx: (hasHeaderArtwork = false, hasForegroundArtwork = false): SxProps<Theme> => ({
      ...(hasHeaderArtwork && {
        minWidth: 0,
        position: 'relative',
        zIndex: 2,
        width: 'fit-content',
        maxWidth: TLP_HEADER_TEXT_MAX_WIDTH
      }),
      ...(hasForegroundArtwork && {
        [TLP_HEADER_FOREGROUND_VISIBLE_QUERY]: {
          gridColumn: '2 / 3',
          justifySelf: 'center',
          width: '100% !important',
          maxWidth: '100% !important'
        }
      })
    }),

    headingSx: (
      fontFamily = TLP_HEADING_FONT_FAMILY,
      hasHeaderArtwork = false,
      hasForegroundArtwork = false
    ): SxProps<Theme> => ({
      fontFamily,
      fontWeight: '700 !important',
      fontSize: {
        xs: '24px !important',
        sm: '28px !important',
        md: '48px !important'
      },
      lineHeight: '1.2 !important',
      color: hasHeaderArtwork ? TLP_BANNER_HERO_TEXT : TLP_HEADER_COLOR,
      overflowWrap: 'break-word',
      ...(hasHeaderArtwork && {
        maxWidth: TLP_HEADER_TEXT_MAX_WIDTH
      }),
      ...(hasForegroundArtwork && {
        [TLP_HEADER_FOREGROUND_VISIBLE_QUERY]: {
          maxWidth: '100%'
        }
      }),
      padding: '0 !important',
      paddingBottom: '0 !important',
      margin: '0 0 6px 0 !important',
      border: 'none !important',
      borderBottom: 'none !important',
      '&::before, &::after': {
        display: 'none !important'
      }
    }),

    detailsSx: (
      fontFamily = TLP_FONT_FAMILY,
      hasHeaderArtwork = false,
      hasForegroundArtwork = false
    ): SxProps<Theme> => ({
      fontFamily,
      fontSize: '16px',
      fontWeight: 400,
      color: hasHeaderArtwork ? TLP_BANNER_HERO_TEXT : TLP_SUB_HEADER_COLOR,
      lineHeight: 1.5,
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      ...(hasHeaderArtwork && {
        width: '100%',
        maxWidth: TLP_HEADER_TEXT_MAX_WIDTH
      }),
      ...(hasForegroundArtwork && {
        [TLP_HEADER_FOREGROUND_VISIBLE_QUERY]: {
          maxWidth: '100%'
        }
      }),
      '& .detail-separator': {
        color: hasHeaderArtwork ? TLP_BANNER_HERO_TEXT : SEPARATOR_GREY,
        fontSize: '20px',
        lineHeight: 1,
        margin: '0 4px',
        opacity: hasHeaderArtwork ? 0.9 : 1
      }
    }),

    innerContainerSx: (hasHeaderArtwork = false, hasForegroundArtwork = false): SxProps<Theme> => ({
      maxWidth: WCAG_MAX_CONTENT_WIDTH,
      margin: '0 auto',
      width: '100% !important',
      position: 'relative',
      zIndex: 2,
      padding: {
        xs: '50px 24px',
        sm: '70px 24px',
        md: '120px 24px'
      },
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100%',
      ...(hasHeaderArtwork && {
        textAlign: 'center'
      }),
      ...(hasForegroundArtwork && {
        [TLP_HEADER_FOREGROUND_VISIBLE_QUERY]: {
          display: 'grid',
          gridTemplateColumns: `${TLP_HEADER_FOREGROUND_COLUMN_WIDTH} minmax(0, 1fr) ${TLP_HEADER_FOREGROUND_COLUMN_WIDTH}`,
          maxWidth: `${TLP_HEADER_FOREGROUND_LAYOUT_WIDTH} !important`,
          padding: '20px 0 !important',
          alignItems: 'center'
        }
      })
    })
  },

  // ---------------------------------------------------------------------------
  // ApplyNow component styles
  // ---------------------------------------------------------------------------
  applyNow: {
    containerSx: (isVisible: boolean): SxProps<Theme> => ({
      maxWidth: WCAG_MAX_CONTENT_WIDTH,
      margin: '2em auto',
      padding: '0 1em',
      position: 'fixed',
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1,
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
      pointerEvents: isVisible ? 'auto' : 'none',
      transition: 'opacity 0.25s ease, transform 0.25s ease'
    }),

    buttonSx: (fontFamily = TLP_FONT_FAMILY): SxProps<Theme> =>
      buildActionButtonSx({
        variant: 'primary',
        fontFamily,
        includeActive: true,
        overrides: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px 24px',
          width: '155px',
          height: '64px',
          fontWeight: 700,
          fontSize: '16px !important',
          color: `${TLP_BUTTON_TEXT_COLOR} !important`,
          backgroundColor: `${TLP_BUTTON_COLOR} !important`,
          textDecoration: 'none !important',
          '&:hover': {
            backgroundColor: `${TLP_BUTTON_HOVER_COLOR} !important`,
            textDecoration: 'none !important',
            color: `${TLP_BUTTON_TEXT_COLOR} !important`,
            boxShadow: 'none'
          },
          '&:focus': {
            backgroundColor: `${TLP_BUTTON_COLOR} !important`,
            textDecoration: 'none !important',
            color: `${TLP_BUTTON_TEXT_COLOR} !important`,
            boxShadow: 'none',
            outline: 'none'
          },
          '&:active': {
            backgroundColor: `${TLP_BUTTON_HOVER_COLOR} !important`,
            textDecoration: 'none !important',
            color: `${TLP_BUTTON_TEXT_COLOR} !important`,
            boxShadow: 'none'
          }
        }
      }),

    centerWrapperSx: (): SxProps<Theme> => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    })
  },

  // AutoFillPersonalDetails component styles
  autoFillPersonalDetails: {
    containerSx: (): SxProps<Theme> => ({
      marginBottom: '32px'
    }),

    headingSx: (fontFamily = TLP_HEADING_FONT_FAMILY): SxProps<Theme> => ({
      color: `${TLP_BUTTON_COLOR} !important`,
      fontFamily,
      fontSize: '16px !important',
      fontWeight: '700 !important',
      lineHeight: '18px',
      // Zero top margin to suppress the browser default `margin-block-start`
      // on h3, which would otherwise add ~16px above the autofill block.
      margin: '0 0 8px 0 !important'
    }),

    descriptionSx: (
      providerCount: number,
      fontFamily = WCAG_DEFAULT_FONT_FAMILY
    ): SxProps<Theme> => ({
      color: AUTO_FILL_BODY,
      fontFamily: `${fontFamily} !important`,
      fontSize: '14px !important',
      fontWeight: 400,
      lineHeight: '18px !important',
      maxWidth: {
        xs: '100%',
        sm: autoFillUploadWidth(providerCount)
      },
      width: {
        xs: '100%',
        sm: autoFillUploadWidth(providerCount)
      }
    }),

    contentRowSx: (providerCount: number): SxProps<Theme> => ({
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      alignItems: {
        xs: 'stretch',
        sm: 'center',
        md: providerCount > 0 ? 'center' : 'flex-start'
      },
      gap: { xs: '16px', md: '24px' },
      marginTop: '32px !important',
      marginBottom: '32px !important',
      width: '100%'
    }),

    uploadCardSx: (
      providerCount: number,
      isDragActive: boolean,
      hasError: boolean,
      isDisabled = false
    ): SxProps<Theme> => ({
      width: {
        xs: '100% !important',
        sm: `${autoFillUploadWidth(providerCount)} !important`
      },
      minWidth: {
        xs: '100% !important',
        sm: `${autoFillUploadWidth(providerCount)} !important`
      },
      maxWidth: {
        xs: '100% !important',
        sm: `${autoFillUploadWidth(providerCount)} !important`
      },
      flexBasis: {
        sm: `${autoFillUploadWidth(providerCount)} !important`
      },
      flexShrink: 0,
      height: 'auto !important',
      minHeight: '131px !important',
      backgroundColor: `${autoFillCardBackgroundColor(hasError, isDragActive)} !important`,
      borderRadius: '8px',
      border: `1px solid ${autoFillCardBorderColor(hasError, isDragActive)} !important`,
      outline: `2px solid ${isDragActive && !hasError ? SCOUT_TEAL : 'transparent'} !important`,
      outlineOffset: '-1px',
      boxSizing: 'border-box',
      boxShadow: 'none !important',
      padding: { xs: '16px 18px !important', md: '32px !important' },
      opacity: isDisabled ? 0.72 : 1,
      transition: 'border-color 0.2s ease, background-color 0.2s ease, outline-color 0.2s ease',
      '&:focus, &:focus-visible': {
        boxShadow: 'none !important',
        outline: '2px solid transparent !important'
      }
    }),

    uploadCardInnerSx: (hasSelectedFile = false): SxProps<Theme> => ({
      display: 'flex',
      flexDirection: 'column',
      alignItems: hasSelectedFile ? 'stretch' : 'center',
      height: '100%',
      justifyContent: 'center',
      minHeight: '100%',
      textAlign: hasSelectedFile ? 'left' : 'center'
    }),

    uploadRowSx: (): SxProps<Theme> => ({
      display: 'flex',
      flexWrap: { xs: 'wrap', sm: 'nowrap' },
      alignItems: 'center',
      justifyContent: 'center',
      columnGap: '8px',
      maxWidth: '100%',
      rowGap: { xs: '8px', sm: 0 }
    }),

    uploadIconSx: (): SxProps<Theme> => ({
      color: `${AUTO_FILL_ICON} !important`,
      flexShrink: 0,
      fontSize: '24px !important'
    }),

    uploadPromptSx: (fontFamily = WCAG_DEFAULT_FONT_FAMILY): SxProps<Theme> => ({
      alignItems: 'center',
      color: `${AUTO_FILL_BODY} !important`,
      display: 'inline-flex',
      fontFamily: `${fontFamily} !important`,
      fontSize: '14px !important',
      fontStyle: 'normal',
      fontWeight: '400 !important',
      lineHeight: '18px !important',
      flexShrink: 0,
      margin: '0 !important',
      minHeight: '32px',
      whiteSpace: 'nowrap'
    }),

    chooseButtonSx: (hasError: boolean): SxProps<Theme> => {
      const buttonTextColor = hasError ? VALIDATION_ERROR_COLOR : TLP_SECONDARY_BUTTON_TEXT_COLOR;
      const buttonBackgroundColor = hasError ? WHITE : TLP_SECONDARY_BUTTON_COLOR;
      const hoverBackgroundColor = hasError ? WHITE : TLP_SECONDARY_BUTTON_HOVER_COLOR;

      return {
        ...buttonBase,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        minWidth: '141px',
        minHeight: '32px',
        width: '141px',
        flexShrink: 0,
        whiteSpace: 'nowrap',
        padding: '8px 16px',
        borderRadius: TLP_BUTTON_RADIUS,
        border: `1px solid ${buttonTextColor}`,
        backgroundColor: buttonBackgroundColor,
        color: buttonTextColor,
        fontFamily: `${WCAG_DEFAULT_FONT_FAMILY} !important`,
        fontSize: '12px !important',
        fontStyle: 'normal',
        fontWeight: '700 !important',
        lineHeight: '16px !important',
        '&:hover': {
          backgroundColor: `${hoverBackgroundColor} !important`,
          borderColor: buttonTextColor,
          color: `${buttonTextColor} !important`,
          boxShadow: 'none'
        },
        '&:hover *': {
          color: `${buttonTextColor} !important`
        },
        '&:focus-visible': {
          outline: `2px solid ${buttonTextColor}`,
          outlineOffset: '2px'
        },
        '&.Mui-disabled': {
          backgroundColor: WHITE,
          borderColor: BORDER_GREY_ALT,
          color: AUTO_FILL_HELPER
        }
      };
    },

    selectedFileContainerSx: (): SxProps<Theme> => ({
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
      width: '100%'
    }),

    selectedFileButtonSx: (hasError: boolean, isDisabled = false): SxProps<Theme> => ({
      appearance: 'none',
      alignItems: 'center',
      backgroundColor: 'rgba(255, 255, 255, 0.72)',
      border: `1px solid ${hasError ? VALIDATION_ERROR_COLOR : 'transparent'}`,
      borderRadius: '8px',
      boxSizing: 'border-box',
      cursor: isDisabled ? 'default' : 'pointer',
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-start',
      height: '66px',
      maxWidth: '100%',
      margin: 0,
      maxHeight: '100%',
      overflow: 'hidden',
      padding: '12px 16px',
      textAlign: 'left',
      transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
      width: '100%',
      '& .wcag-autofill-selected-file-icon-tile': {
        backgroundColor: WHITE,
        color: AUTO_FILL_ICON
      },
      '& .wcag-autofill-selected-file-action-icon, & .wcag-autofill-selected-file-action-label': {
        opacity: 0,
        pointerEvents: 'none'
      },
      '& .wcag-autofill-selected-file-doc-icon, & .wcag-autofill-selected-file-filename': {
        opacity: 1
      },
      '&:hover': {
        backgroundColor: WHITE,
        borderColor: hasError ? VALIDATION_ERROR_COLOR : SCOUT_TEAL
      },
      '&:hover .wcag-autofill-selected-file-icon-tile': {
        backgroundColor: '#FFF4F4',
        color: AUTO_FILL_CLEAR_ICON
      },
      '&:hover .wcag-autofill-selected-file-doc-icon, &:hover .wcag-autofill-selected-file-filename':
        {
          opacity: 0
        },
      '&:hover .wcag-autofill-selected-file-action-icon, &:hover .wcag-autofill-selected-file-action-label':
        {
          opacity: 1,
          pointerEvents: 'auto'
        },
      '&:focus-visible .wcag-autofill-selected-file-icon-tile': {
        backgroundColor: '#FFF4F4',
        color: AUTO_FILL_CLEAR_ICON
      },
      '&:focus-visible .wcag-autofill-selected-file-doc-icon, &:focus-visible .wcag-autofill-selected-file-filename':
        {
          opacity: 0
        },
      '&:focus-visible .wcag-autofill-selected-file-action-icon, &:focus-visible .wcag-autofill-selected-file-action-label':
        {
          opacity: 1,
          pointerEvents: 'auto'
        },
      '&:focus-visible': {
        backgroundColor: WHITE,
        borderColor: hasError ? VALIDATION_ERROR_COLOR : SCOUT_TEAL,
        ...focusRing(hasError, false)
      },
      '&:disabled': {
        backgroundColor: 'rgba(255, 255, 255, 0.72)',
        borderColor: hasError ? VALIDATION_ERROR_COLOR : 'transparent'
      }
    }),

    selectedFileIconTileSx: (): SxProps<Theme> => ({
      borderRadius: '6px',
      display: 'grid',
      flexShrink: 0,
      height: '40px',
      placeItems: 'center',
      transition: 'background-color 0.2s ease, color 0.2s ease',
      width: '40px'
    }),

    selectedFileIconLayerSx: (): SxProps<Theme> => ({
      alignItems: 'center',
      display: 'inline-flex',
      gridArea: '1 / 1',
      justifyContent: 'center',
      lineHeight: 0,
      transition: 'opacity 0.2s ease'
    }),

    selectedFileDocTypeIconSx: (): SxProps<Theme> => ({
      alignItems: 'center',
      display: 'inline-flex',
      justifyContent: 'center',
      lineHeight: 0
    }),

    selectedFileClearIconSx: (): SxProps<Theme> => ({
      color: `${AUTO_FILL_CLEAR_ICON} !important`,
      fontSize: '22px !important'
    }),

    selectedFileTextGroupSx: (): SxProps<Theme> => ({
      display: 'flex',
      flex: '1 1 auto',
      flexDirection: 'column',
      gap: '4px',
      minWidth: 0,
      overflow: 'hidden'
    }),

    selectedFilePrimaryTextSx: (): SxProps<Theme> => ({
      display: 'grid',
      minHeight: '18px',
      minWidth: 0,
      width: '100%'
    }),

    selectedFileNameSx: (
      fontFamily = WCAG_DEFAULT_FONT_FAMILY,
      color = SCOUT_BLUE
    ): SxProps<Theme> => ({
      color: `${color} !important`,
      display: 'block',
      fontFamily: `${fontFamily} !important`,
      fontSize: '14px !important',
      fontWeight: '700 !important',
      lineHeight: '18px !important',
      gridArea: '1 / 1',
      margin: '0 !important',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      transition: 'opacity 0.2s ease',
      whiteSpace: 'nowrap',
      width: '100%'
    }),

    selectedFileActionLabelSx: (fontFamily = WCAG_DEFAULT_FONT_FAMILY): SxProps<Theme> => ({
      color: `${AUTO_FILL_ACTION} !important`,
      display: 'block',
      fontFamily: `${fontFamily} !important`,
      fontSize: '14px !important',
      fontWeight: '700 !important',
      lineHeight: '18px !important',
      gridArea: '1 / 1',
      margin: '0 !important',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      transition: 'opacity 0.2s ease',
      whiteSpace: 'nowrap',
      width: '100%'
    }),

    selectedFileMetaSx: (fontFamily = WCAG_DEFAULT_FONT_FAMILY): SxProps<Theme> => ({
      color: `${AUTO_FILL_HELPER} !important`,
      display: 'block',
      fontFamily: `${fontFamily} !important`,
      fontSize: '12px !important',
      fontWeight: '400 !important',
      lineHeight: '16px !important',
      margin: '0 !important'
    }),

    helperTextSx: (
      fontFamily = WCAG_DEFAULT_FONT_FAMILY,
      marginTop = '8px !important'
    ): SxProps<Theme> => ({
      color: `${AUTO_FILL_HELPER} !important`,
      fontFamily: `${fontFamily} !important`,
      fontSize: '10px !important',
      fontWeight: '400 !important',
      lineHeight: '14px !important',
      marginTop,
      textAlign: 'center',
      whiteSpace: 'nowrap'
    }),

    orTextSx: (): SxProps<Theme> => ({
      display: { xs: 'none', md: 'block' },
      color: AUTO_FILL_OR,
      flexShrink: 0,
      fontFamily: WCAG_DEFAULT_FONT_FAMILY,
      fontSize: '18px',
      fontWeight: 400,
      lineHeight: '18px',
      textAlign: 'center'
    }),

    providersGridSx: (providerCount: number): SxProps<Theme> => ({
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        sm: providerCount > 1 ? 'repeat(2, max-content)' : 'max-content',
        md: providerCount > 1 ? 'repeat(2, max-content)' : 'max-content'
      },
      gap: '12px',
      width: {
        xs: '100%',
        sm: 'fit-content',
        md: 'fit-content'
      },
      maxWidth: {
        xs: '100%',
        sm: 'fit-content',
        md: 'fit-content'
      },
      flexBasis: {
        xs: 'auto',
        sm: 'auto',
        md: 'auto'
      }
    }),

    providerButtonSx: (kind: AutoFillProviderKind, providerCount: number): SxProps<Theme> => ({
      ...autoFillProviderControlBaseSx(kind, providerCount),
      '&:hover': {
        backgroundColor: autoFillProviderHoverBackgroundColor(kind),
        boxShadow: 'none'
      },
      '&:focus-visible': {
        ...autoFillProviderFocusVisibleSx
      },
      '&.Mui-disabled': {
        ...autoFillProviderDisabledSx(kind)
      }
    }),

    indeedShellSx: (providerCount: number): SxProps<Theme> => ({
      ...autoFillProviderControlBaseSx('indeed', providerCount),
      // The legacy job template reset clears <div> backgrounds and padding under #job.
      // Keep these shell styles important so the React button stays visible and spaced.
      backgroundColor: `${AUTO_FILL_INDEED} !important`,
      padding: '12px 16px !important',
      position: 'relative',
      '&:hover': {
        backgroundColor: `${autoFillProviderHoverBackgroundColor('indeed')} !important`,
        boxShadow: 'none'
      },
      '&:focus-within': {
        ...autoFillProviderFocusVisibleSx
      },
      '&[data-disabled="true"]': {
        ...autoFillProviderDisabledSx('indeed'),
        backgroundColor: `${AUTO_FILL_INDEED} !important`,
        cursor: 'default'
      }
    }),

    indeedShellContentSx: (): SxProps<Theme> => ({
      position: 'relative',
      zIndex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: autoFillProviderGap('indeed'),
      width: '100%',
      pointerEvents: 'none'
    }),

    indeedOverlaySlotSx: (): SxProps<Theme> => ({
      position: 'absolute',
      inset: 0,
      zIndex: 2,
      cursor: 'pointer',
      '& #apply-with-indeed': {
        position: 'absolute !important',
        inset: '0 !important',
        width: '100% !important',
        height: '100% !important',
        margin: '0 !important',
        cursor: 'pointer !important'
      },
      '& #apply-with-indeed > .indeed-apply-widget, & #apply-with-indeed > [data-indeed-apply-widget-type="AWI"]':
        {
          width: '100% !important',
          height: '100% !important',
          cursor: 'pointer !important'
        },
      '& #apply-with-indeed .indeed-apply-button': {
        display: 'block !important',
        width: '100% !important',
        height: '100% !important',
        minWidth: '0 !important',
        margin: '0 !important',
        opacity: '0 !important',
        cursor: 'pointer !important'
      },
      '& #apply-with-indeed [data-indeed-apply-widget-type="AWI"]': {
        display: 'block !important',
        width: '100% !important',
        height: '100% !important',
        cursor: 'pointer !important'
      },
      '& #apply-with-indeed .ia-btn-div, & #apply-with-indeed .apply-with-indeed-button-container':
        {
          display: 'flex !important',
          alignItems: 'stretch !important',
          justifyContent: 'stretch !important',
          width: '100% !important',
          height: '100% !important',
          minWidth: '0 !important',
          minHeight: '0 !important',
          margin: '0 !important',
          opacity: '0 !important',
          cursor: 'pointer !important'
        },
      '& #apply-with-indeed button, & #apply-with-indeed a, & #apply-with-indeed [role="button"]': {
        width: '100% !important',
        height: '100% !important',
        minWidth: '0 !important',
        minHeight: '0 !important',
        margin: '0 !important',
        opacity: '0 !important',
        cursor: 'pointer !important'
      },
      '& #apply-with-indeed button:focus-visible, & #apply-with-indeed a:focus-visible, & #apply-with-indeed [role="button"]:focus-visible':
        {
          outline: 'none !important',
          boxShadow: 'none !important'
        }
    }),

    indeedOverlayBlockerSx: (): SxProps<Theme> => ({
      position: 'absolute',
      inset: 0,
      zIndex: 3,
      cursor: 'default'
    }),

    providerPrefixSx: (): SxProps<Theme> => ({
      color: WHITE,
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: '20px'
    }),

    providerLabelSx: (): SxProps<Theme> => ({
      color: `${WHITE} !important`,
      fontFamily: `${WCAG_DEFAULT_FONT_FAMILY} !important`,
      fontSize: '14px !important',
      fontStyle: 'normal',
      fontWeight: '600 !important',
      lineHeight: 'normal !important',
      textAlign: 'center'
    }),

    providerBrandGroupSx: (): SxProps<Theme> => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      flexShrink: 0
    }),

    providerDividerSx: (): SxProps<Theme> => ({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      width: '1px',
      height: '16px',
      '& svg, & img': {
        display: 'block',
        width: '1px',
        height: '16px'
      }
    }),

    providerLogoSx: (kind: AutoFillProviderKind): SxProps<Theme> => ({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      width: '16px',
      height: '16px',
      overflow: kind === 'seek' ? 'hidden' : 'visible',
      '& svg, & img': {
        display: 'block',
        width: kind === 'seek' ? '52.3px' : 'auto',
        maxWidth: kind === 'seek' ? 'none' : '100%',
        height: '100%'
      }
    }),

    errorSx: (): SxProps<Theme> => ({
      ...validationErrorMessageSx(),
      maxWidth: WCAG_MAX_CONTENT_WIDTH
    })
  },

  // CandidateDetails component styles
  candidateDetails: {
    containerSx: (): SxProps<Theme> => ({
      maxWidth: WCAG_MAX_CONTENT_WIDTH,
      margin: '0 auto 80px auto',
      padding: 0,
      scrollMarginTop: '100px'
    }),

    headingRowSx: (): SxProps<Theme> => ({
      display: 'flex',
      alignItems: { xs: 'flex-start', sm: 'center' },
      justifyContent: 'flex-start',
      flexWrap: 'wrap',
      gap: '24px',
      marginBottom: '32px !important'
    }),

    parsingIndicatorSx: (): SxProps<Theme> => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px'
    }),

    parsingProgressWrapperSx: (): SxProps<Theme> => ({
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '32px',
      height: '32px',
      flexShrink: 0
    }),

    parsingProgressCircleSx: (): SxProps<Theme> => ({
      color: SCOUT_TEAL,
      '& .MuiCircularProgress-circle': {
        strokeLinecap: 'round'
      }
    }),

    parsingProgressLabelSx: (): SxProps<Theme> => ({
      position: 'absolute',
      color: `${SCOUT_BLUE} !important`,
      fontFamily: `${WCAG_DEFAULT_FONT_FAMILY} !important`,
      fontSize: '10px !important',
      fontWeight: '700 !important',
      lineHeight: '10px !important'
    }),

    parsingStatusTextSx: (): SxProps<Theme> => ({
      alignItems: 'center',
      color: `${AUTO_FILL_HELPER} !important`,
      display: 'inline-flex',
      fontFamily: `${WCAG_DEFAULT_FONT_FAMILY} !important`,
      fontSize: '14px !important',
      fontStyle: 'italic',
      fontWeight: '400 !important',
      lineHeight: '18px !important',
      margin: '0 !important',
      minHeight: '32px'
    }),

    countriesErrorAlertSx: (): SxProps<Theme> => ({
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
      borderRadius: '4px',
      color: '#dc2626',
      marginBottom: '16px',
      padding: '12px 16px'
    }),

    countriesErrorMessageSx: (): SxProps<Theme> => ({
      fontSize: '14px',
      marginBottom: '8px'
    }),

    countriesErrorRetryButtonSx: (): SxProps<Theme> => ({
      backgroundColor: '#dc2626',
      border: 'none',
      borderRadius: '4px',
      color: WHITE,
      cursor: 'pointer',
      fontSize: '12px !important',
      padding: '8px 16px'
    }),

    formFieldsSx: (fontFamily = WCAG_DEFAULT_FONT_FAMILY): SxProps<Theme> => ({
      '& .candidate-form-fields': {
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        columnGap: { xs: 0, sm: '32px' },
        rowGap: '32px !important'
      },
      '& .name-fields, & .contact-fields, & .address-fields': {
        display: 'contents'
      },
      // Title field owns a row at half-width; :has(.candidate-select) limits it
      // to when the Title field renders (job.options.title_field).
      '& .name-fields > .form-field:first-child:has(> .candidate-select)': {
        gridColumn: { sm: '1 / -1' }
      },
      // Cap the Title select to one column width (half grid minus half columnGap 32px).
      '& .name-fields > .form-field:first-child:has(> .candidate-select) > .candidate-select': {
        maxWidth: { sm: 'calc(50% - 16px)' }
      },
      '& .form-field': {
        display: 'flex !important',
        flexDirection: 'column !important',
        // Avoid flex gap so injected password-manager nodes do not create
        // extra spacing between the label, control, and validation content.
        '& .field-label': {
          display: 'inline-block !important',
          fontFamily,
          fontWeight: '400 !important',
          fontSize: '16px !important',
          color: `${BLACK} !important`,
          margin: '0 0 16px 0 !important',
          whiteSpace: 'nowrap !important',
          '& .required-indicator': {
            color: `${REQUIRED_RED} !important`,
            fontWeight: '400 !important',
            display: 'inline !important',
            marginLeft: '4px !important'
          }
        },
        '& .field-hint': {
          fontFamily,
          fontSize: '14px !important',
          color: `${DARK_GREY} !important`,
          fontWeight: '400 !important',
          marginTop: '4px !important'
        }
      },
      '& .candidate-input': {
        width: '100% !important',
        borderRadius: '8px !important',
        border: `1px solid ${BORDER_GREY} !important`,
        padding: '12px !important',
        fontFamily,
        fontSize: '16px !important',
        fontWeight: 400,
        color: `${BLACK} !important`,
        backgroundColor: `${WHITE} !important`,
        minHeight: '44px !important',
        boxSizing: 'border-box !important',
        '&::placeholder': {
          color: `${PLACEHOLDER_GREY} !important`
        },
        '&:focus, &:focus-visible': focusRing()
      },
      '& .candidate-select': {
        width: '100% !important',
        height: '50px !important',
        minHeight: '44px !important',
        borderRadius: '8px !important',
        border: `1px solid ${BORDER_GREY} !important`,
        color: `${BLACK} !important`,
        padding: '12px !important',
        paddingRight: '40px !important',
        appearance: 'none !important',
        WebkitAppearance: 'none !important',
        MozAppearance: 'none !important',
        backgroundColor: `${WHITE} !important`,
        backgroundImage:
          "url(\"data:image/svg+xml;charset=UTF-8,%3csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M1 1L6 6L11 1' stroke='%23666666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3e%3c/svg%3e\")",
        backgroundRepeat: 'no-repeat !important',
        backgroundPosition: 'right 16px center !important',
        cursor: 'pointer !important',
        fontFamily,
        fontSize: '16px !important',
        fontWeight: 400,
        boxSizing: 'border-box !important',
        // Show placeholder color when the blank option is selected
        '&:has(option[value=""]:checked)': {
          color: `${PLACEHOLDER_GREY} !important`
        },
        '&:focus, &:focus-visible': focusRing(),
        '& option': {
          color: `${BLACK} !important`
        },
        '& option[value=""]': {
          color: `${PLACEHOLDER_GREY} !important`
        }
      },
      // CandidateDetails uses imperative aria-invalid + inline border-color on phone/email.
      // Keep invalid focus treatment aligned with shared WCAG red focus style.
      '& .candidate-input[aria-invalid="true"], & .candidate-select[aria-invalid="true"]': {
        borderColor: `${VALIDATION_ERROR_COLOR} !important`,
        '&:focus, &:focus-visible': focusRing(true)
      }
      // Browser default outline kept as fallback for injected candidate controls
      // lacking explicit focus styles; MUI controls suppress it via SCSS.
    }),

    contentWrapperSx: (): SxProps<Theme> => ({
      width: '100%',
      padding: '24px 0'
    }),

    phoneErrorCssText: validationErrorCssText,
    phoneErrorBorderColor: validationInvalidBorderColor
  },

  // FormActions component styles
  formActions: {
    actionButtonsSx: (): SxProps<Theme> => ({
      maxWidth: WCAG_MAX_CONTENT_WIDTH,
      margin: '0 auto',
      padding: '8px 0 40px 0 !important',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '16px',
      width: '100%',
      boxSizing: 'border-box'
    }),

    saveButtonSx: (fontFamily = WCAG_DEFAULT_FONT_FAMILY): SxProps<Theme> =>
      buildActionButtonSx({
        variant: 'secondary',
        fontFamily,
        includeFocusVisible: true,
        includeActive: true
      }),

    nextButtonSx: (fontFamily = WCAG_DEFAULT_FONT_FAMILY): SxProps<Theme> =>
      buildActionButtonSx({
        variant: 'primary',
        fontFamily,
        includeFocusVisible: true,
        includeActive: true,
        overrides: {
          // Keeps Next aligned right when Save for later is hidden.
          marginLeft: 'auto'
        }
      }),

    sectionContainerSx: (): SxProps<Theme> => ({
      maxWidth: WCAG_MAX_CONTENT_WIDTH,
      margin: '0 auto',
      // 8px on padding-top (not margin) so it doesn't collapse with the previous
      // section's marginBottom — 88px additive design gap via sectionContainerBase.
      // !important defeats `body[data-wcag-template='true'] #job section { padding: 0 }` in wcag.scss.
      padding: '8px 0 0 !important',
      width: '100%',
      boxSizing: 'border-box'
    })
  },

  saveDialog: {
    paperSx: dialogPaperBaseSx,

    titleSx: dialogTitleBaseSx,

    contentSx: dialogContentBaseSx,

    messageSx: (): SxProps<Theme> => ({
      ...saveDialogCopyBase
    }),

    noteSx: (): SxProps<Theme> => ({
      ...saveDialogCopyBase,
      marginTop: '16px'
    }),

    actionsSx: dialogActionsBaseSx,

    cancelButtonSx: (): SxProps<Theme> =>
      buildActionButtonSx({
        variant: 'secondary',
        fontFamily: WCAG_DEFAULT_FONT_FAMILY,
        includeFocusVisible: true,
        includeActive: true,
        overrides: {
          width: '96px',
          minWidth: '96px'
        }
      }),

    confirmButtonSx: (): SxProps<Theme> =>
      buildActionButtonSx({
        variant: 'primary',
        fontFamily: WCAG_DEFAULT_FONT_FAMILY,
        includeFocusVisible: true,
        includeActive: true,
        overrides: {
          width: '96px',
          minWidth: '96px'
        }
      })
  },

  startOverDialog: {
    paperSx: (): SxProps<Theme> => ({
      ...dialogPaperBaseSx(),
      minHeight: '237px'
    }),

    titleSx: dialogTitleBaseSx,

    contentSx: dialogContentBaseSx,

    messageSx: (): SxProps<Theme> => ({
      ...saveDialogCopyBase
    }),

    actionsSx: dialogActionsBaseSx,

    cancelButtonSx: (): SxProps<Theme> =>
      buildActionButtonSx({
        variant: 'secondary',
        fontFamily: WCAG_DEFAULT_FONT_FAMILY,
        includeFocusVisible: true,
        includeActive: true,
        overrides: {
          width: '96px',
          minWidth: '96px'
        }
      }),

    confirmButtonSx: (): SxProps<Theme> =>
      buildActionButtonSx({
        variant: 'primary',
        fontFamily: WCAG_DEFAULT_FONT_FAMILY,
        includeFocusVisible: true,
        includeActive: true,
        overrides: {
          width: '128px',
          minWidth: '128px',
          whiteSpace: 'nowrap !important'
        }
      })
  },

  // AboutUs component styles
  aboutUs: {
    containerSx: (): SxProps<Theme> => ({
      width: '100vw !important',
      margin: '0 calc(50% - 50vw) 56px !important',
      padding: '40px 0 !important',
      backgroundColor: TLP_ABOUT_BG,
      position: 'relative'
    }),

    innerContainerSx: (): SxProps<Theme> => ({
      maxWidth: { xs: '365px', sm: '690px', md: WCAG_MAX_CONTENT_WIDTH },
      width: '100%',
      margin: '0 auto',
      padding: { xs: '0 1em', sm: '0 1em', md: '0' },
      boxSizing: 'border-box'
    }),

    contentSx: (): SxProps<Theme> => ({
      padding: {
        xs: '24px',
        sm: '24px 48px',
        md: '24px 48px'
      },
      minHeight: '292px',
      width: '100%',
      boxSizing: 'border-box'
    }),

    bodySx: (): SxProps<Theme> => ({
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '24px',
      width: '100%',
      boxSizing: 'border-box'
    }),

    videoContainerSx: (): SxProps<Theme> => ({
      flex: 1,
      width: { xs: '100%', md: 'auto' },
      height: { xs: '300px', md: '350px' },
      maxHeight: { xs: '300px', md: '400px' },
      minWidth: 0,
      minHeight: { xs: '300px', md: '350px' },
      borderRadius: '8px',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: { xs: 'center', md: 'flex-start' },
      '@media (max-width: 768px)': {
        width: '100%',
        height: '300px',
        minHeight: '300px',
        maxHeight: '300px'
      }
    }),

    videoStyle: (): React.CSSProperties => ({
      width: '100%',
      height: '100%',
      minHeight: '100%',
      border: 0,
      borderRadius: '8px'
    }),

    imageContainerSx: (): SxProps<Theme> => ({
      flex: 1,
      width: { xs: '100%', md: 'auto' },
      height: { xs: '300px', md: '350px' },
      maxHeight: { xs: '300px', md: '400px' },
      minWidth: 0,
      minHeight: { xs: '300px', md: '350px' },
      borderRadius: '8px',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: { xs: 'center', md: 'flex-start' }
    }),

    imageSx: (): SxProps<Theme> => ({
      width: '100%',
      height: '100%',
      display: 'block',
      objectFit: 'cover',
      borderRadius: '8px'
    }),

    invalidVideoFallbackSx: (fontFamily = WCAG_DEFAULT_FONT_FAMILY): SxProps<Theme> => ({
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: BORDER_GREY_ALT,
      color: DARK_GREY,
      fontFamily,
      fontSize: '14px',
      textAlign: 'center',
      padding: '20px'
    }),

    textColumnSx: (): SxProps<Theme> => ({
      flex: 1,
      paddingRight: { xs: 0, md: '12px' },
      minWidth: 0
    }),

    htmlContentSx: (fontFamily = TLP_FONT_FAMILY): SxProps<Theme> => ({
      fontFamily,
      fontSize: '16px',
      lineHeight: 1.5,
      color: TLP_ABOUT_TEXT
      // Element-level typography is owned by the WCAG-scoped rules +
      // `.wcag-rich-text` block in wcag.scss.
    })
  },

  // SourceSelection component styles
  sourceSelection: {
    containerSx: (): SxProps<Theme> => ({
      ...sectionContainerBase
    }),

    sourceFieldContainerSx: (): SxProps<Theme> => ({
      maxWidth: { xs: '367px', sm: '100%' },
      '& .MuiFormControl-root': {
        width: '100% !important'
      }
    }),

    fieldLabelSx: (): SxProps<Theme> => ({
      display: 'block',
      marginBottom: '16px !important'
    }),

    // Delegates to shared validation token for unified error presentation
    sourceErrorSx: (): SxProps<Theme> => ({
      ...validationErrorMessageSx(),
      display: 'block'
    })

    // SourceSelection currently only supports dropdown source input.
  },

  automatedDecisionNotice: {
    containerSx: (): SxProps<Theme> => ({ ...sectionContainerBase }),
    bodySx: (): SxProps<Theme> => ({
      fontFamily: WCAG_DEFAULT_FONT_FAMILY,
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: 1.5,
      color: BLACK,
      whiteSpace: 'pre-line',
      margin: 0
    })
  },

  // PrivacyAcknowledgement component styles
  privacy: {
    containerSx: (): SxProps<Theme> => ({
      ...sectionContainerBase
    }),

    descriptionSx: (): SxProps<Theme> => ({
      fontFamily: WCAG_DEFAULT_FONT_FAMILY,
      fontSize: '16px',
      fontWeight: 400,
      color: BLACK,
      marginBottom: '32px !important'
      // <p>/<ul>/<ol>/<li> typography and trailing-margin reset are owned by
      // wcag.scss (.wcag-rich-text block); the wrapper sets container styles only.
    }),

    checkboxRowSx: (): SxProps<Theme> => ({
      margin: 0,
      alignItems: 'flex-start',
      gap: '16px',
      '& .MuiFormControlLabel-label': {
        fontFamily: WCAG_DEFAULT_FONT_FAMILY,
        fontSize: '16px',
        color: BLACK,
        lineHeight: 1.5
      }
    }),

    checkboxSx: (): SxProps<Theme> => ({
      padding: '0 !important',
      color: CHECKBOX_UNCHECKED_GREY,
      '&.Mui-checked': {
        color: `${SCOUT_TEAL} !important`
      },
      '& .MuiSvgIcon-root': {
        width: '22px !important',
        height: '22px !important',
        fontSize: '22px !important'
      },
      '&.Mui-focusVisible, &:focus-visible': {
        outline: `2px solid ${SCOUT_TEAL} !important`,
        outlineOffset: '2px',
        borderRadius: '4px'
      }
    }),

    // Delegates to shared validation token for unified error presentation;
    // shape mirrors sourceSelection.sourceErrorSx for consistency.
    errorSx: (): SxProps<Theme> => ({
      ...validationErrorMessageSx(),
      display: 'block'
    })
  },

  // Attachments component styles
  attachments: {
    containerSx: (): SxProps<Theme> => ({
      ...sectionContainerBase
    }),

    globalHintSx: (): SxProps<Theme> => ({
      display: 'block',
      fontSize: '13px !important',
      lineHeight: 1.5,
      color: '#5A5A5A',
      backgroundColor: '#F7FBFB',
      border: '1px solid #E8F1F1',
      borderRadius: '8px',
      padding: '10px 12px',
      marginBottom: '32px !important'
    }),

    itemContainerSx: (): SxProps<Theme> => ({
      marginBottom: '24px !important',
      '&:last-child': {
        marginBottom: 0
      }
    }),

    labelSx: (): SxProps<Theme> => ({
      display: 'block',
      fontWeight: 600,
      fontSize: '16px',
      color: BLACK,
      marginBottom: '16px !important'
    }),

    fileInputRowSx: (): SxProps<Theme> => ({
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    }),

    browseButtonSx: (hasError = false): SxProps<Theme> => {
      const focusOutlineColor = hasError ? VALIDATION_ERROR_COLOR : TLP_BUTTON_COLOR;

      return {
        padding: '16px 8px !important',
        maxHeight: `${ATTACHMENT_CONTROL_HEIGHT} !important`,
        minWidth: '95px !important',
        fontWeight: 600,
        fontSize: '12px !important',
        color: `${TLP_BUTTON_TEXT_COLOR} !important`,
        WebkitTextFillColor: TLP_BUTTON_TEXT_COLOR,
        backgroundColor: `${TLP_BUTTON_COLOR} !important`,
        border: 'none !important',
        borderRadius: TLP_BUTTON_RADIUS,
        textTransform: 'none',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: `${TLP_BUTTON_HOVER_COLOR} !important`,
          color: `${TLP_BUTTON_TEXT_COLOR} !important`,
          WebkitTextFillColor: TLP_BUTTON_TEXT_COLOR,
          opacity: 1
        },
        '&:hover *': {
          color: `${TLP_BUTTON_TEXT_COLOR} !important`,
          WebkitTextFillColor: TLP_BUTTON_TEXT_COLOR
        },
        // Explicit focus ring: MUI Button emotion styles beat the global
        // button:focus-visible rule in wcag.scss, so set it here (matches
        // selectFieldStyles / formFieldsSx).
        '&.Mui-focusVisible, &:focus-visible': {
          outline: `2px solid ${focusOutlineColor} !important`,
          outlineOffset: '2px'
        }
      };
    },

    fileNameDisplaySx: (): SxProps<Theme> => ({
      alignItems: 'center',
      color: BLACK,
      display: 'inline-flex',
      fontSize: '16px',
      lineHeight: 1.5,
      margin: '0 !important',
      minHeight: ATTACHMENT_CONTROL_HEIGHT
    }),

    // Delegates to shared validation token for unified error presentation
    errorSx: (): SxProps<Theme> => ({
      ...validationErrorMessageSx(),
      display: 'block'
    }),

    removeButtonSx: (): SxProps<Theme> => ({
      alignItems: 'center',
      display: 'inline-flex',
      fontSize: '24px !important',
      height: ATTACHMENT_CONTROL_HEIGHT,
      justifyContent: 'center',
      padding: '0 !important',
      width: ATTACHMENT_CONTROL_HEIGHT,
      '& .MuiSvgIcon-root': {
        color: CLEAR_ICON_COLOR,
        fontSize: 'inherit'
      }
    })
  },

  // Preview component styles
  preview: {
    // wcag.scss gives the top banner a 40px margin-bottom; zero padding-top so the
    // alert sits at the preview box's top edge, and padding-bottom so FormActions
    // owns the gap to the buttons.
    containerSx: (): SxProps<Theme> => ({
      ...sectionContainerBase,
      marginBottom: 0,
      paddingTop: 0,
      paddingBottom: 0
    }),

    sectionSx: (): SxProps<Theme> => ({
      marginBottom: '80px !important',
      '&:last-child': {
        marginBottom: '0 !important'
      }
    }),

    // Privacy preview renders differently (heading + rich-text description +
    // static "Yes I agree" confirmation), so it owns its own typography; the
    // heading still uses the shared sectionHeading.rootSx contract.
    privacyDescriptionSx: (): SxProps<Theme> => ({
      fontFamily: WCAG_DEFAULT_FONT_FAMILY,
      fontSize: '16px',
      fontWeight: 400,
      color: BLACK,
      marginBottom: '40px !important'
      // Element-level typography is owned by the WCAG-scoped rules +
      // `.wcag-rich-text` block in wcag.scss.
    }),

    privacyAgreeLineSx: (): SxProps<Theme> => ({
      fontFamily: WCAG_DEFAULT_FONT_FAMILY,
      fontSize: '16px',
      fontWeight: '600 !important',
      color: WCAG_TEXT_DARK
    }),

    infoBannerSx: (): SxProps<Theme> => ({
      display: 'grid',
      gridTemplateColumns: '22px minmax(0, 1fr)',
      width: '100%',
      alignItems: 'center',
      columnGap: '16px',
      boxSizing: 'border-box',
      padding: '12px 16px !important',
      background: `${INFO_BANNER_BG} !important`,
      backgroundColor: `${INFO_BANNER_BG} !important`,
      borderRadius: '4px',
      marginBottom: '40px !important',
      color: `${INFO_BANNER_TEXT} !important`,
      '& .MuiAlert-icon': {
        display: 'inline-flex',
        justifyContent: 'center',
        width: '22px',
        height: '22px',
        marginRight: 0,
        marginTop: 0,
        padding: 0,
        color: `${INFO_BANNER_ICON} !important`,
        alignItems: 'center',
        alignSelf: 'center',
        '& svg': {
          width: '22px',
          height: '22px'
        }
      },
      '& .MuiAlert-message': {
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        minHeight: '40px'
      }
    }),

    infoBannerTextSx: (): SxProps<Theme> => ({
      margin: '0 !important',
      fontFamily: WCAG_DEFAULT_FONT_FAMILY,
      fontSize: '14px !important',
      fontWeight: 400,
      lineHeight: '20px',
      letterSpacing: 'var(--tlp-letter-spacing, 0.15px) !important',
      whiteSpace: 'pre-line',
      color: `${INFO_BANNER_TEXT} !important`
    }),

    fieldGridSx: (): SxProps<Theme> => ({
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
      columnGap: { xs: '0', sm: '32px' },
      rowGap: { xs: '24px', sm: '48px' }
    }),

    fieldItemSx: (fullWidth = false): SxProps<Theme> => ({
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
      ...(fullWidth ? { gridColumn: '1 / -1' } : {})
    }),

    fieldLabelSx: (): SxProps<Theme> => ({
      fontSize: '16px',
      fontWeight: '600 !important',
      lineHeight: '20px',
      color: BLACK,
      '& .wcag-rich-text p': {
        margin: 0,
        fontWeight: 'inherit',
        fontSize: 'inherit',
        lineHeight: 'inherit'
      }
    }),

    requiredAsteriskSx: (): SxProps<Theme> => ({
      color: REQUIRED_RED,
      fontWeight: 400
    }),

    fieldValueSx: (isEmpty: boolean): SxProps<Theme> => ({
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '20px',
      whiteSpace: 'pre-line',
      fontStyle: isEmpty ? 'italic !important' : 'normal !important',
      color: isEmpty ? EMPTY_VALUE_GREY : BLACK
    }),

    questionFieldSx: (): SxProps<Theme> => ({
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
      marginBottom: '48px !important',
      '&:last-child': {
        marginBottom: '0 !important'
      }
    }),

    actionsSx: (): SxProps<Theme> => ({
      maxWidth: WCAG_MAX_CONTENT_WIDTH,
      margin: '0 auto',
      marginTop: '96px !important',
      // 40px gap to footer divider owned by margin-bottom (the old padding-bottom
      // double-counted with wcag.scss's #wcag-preview-actions rule).
      marginBottom: '40px !important',
      padding: 0,
      display: 'flex',
      justifyContent: 'flex-start !important',
      alignItems: 'center',
      gap: '16px',
      width: '100%',
      boxSizing: 'border-box'
    }),

    editButtonSx: (): SxProps<Theme> =>
      buildActionButtonSx({
        variant: 'secondary',
        preset: 'preview',
        fontFamily: WCAG_DEFAULT_FONT_FAMILY
      }),

    startOverButtonSx: (): SxProps<Theme> =>
      buildActionButtonSx({
        variant: 'secondary',
        preset: 'preview',
        fontFamily: WCAG_DEFAULT_FONT_FAMILY,
        overrides: {
          marginRight: 'auto !important'
        }
      }),

    confirmButtonSx: (): SxProps<Theme> =>
      buildActionButtonSx({
        variant: 'primary',
        preset: 'preview',
        fontFamily: WCAG_DEFAULT_FONT_FAMILY
      })
  },

  // PageLoader component styles
  pageLoader: {
    loadingContainerSx: (): SxProps<Theme> => ({
      minHeight: '100vh',
      backgroundColor: WHITE
    })
  }
};
